import React, { useState, useEffect } from 'react'
import { useAppContext } from '../../context/AppContext'
import { useAuthContext } from '../../context/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { apiClient } from '../../services/apiClient'
import { UserSettings } from '../UserSettings'
import CreateWorkspaceModal from '../Organization/CreateWorkspaceModal'
import './ChatHistorySidebar.css'

function ChatHistorySidebar({ isOpen, onToggle }) {
  const { state, dispatch, actions } = useAppContext()
  const { user } = useAuthContext()
  const { hasPermission } = usePermissions()
  const [chatSessions, setChatSessions] = useState([])
  const [editingSession, setEditingSession] = useState(null)
  const [newSessionTitle, setNewSessionTitle] = useState('')
  const [menuOpenFor, setMenuOpenFor] = useState(null)
  const [showUserSettings, setShowUserSettings] = useState(false)
  const [workspaces, setWorkspaces] = useState([])
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false)
  const canCreateWorkspace = hasPermission('workspaces', 'write')

  // Load chat sessions from database
  useEffect(() => {
    const loadChatSessions = async () => {
      try {
        const activeWorkspaceId = state.activeWorkspace?._id || state.activeWorkspace?.id || null
        const response = await apiClient.getChatSessions(50, activeWorkspaceId)
        const sessions = response.sessions || []
        setChatSessions(sessions)
        
        if (!state.currentSessionId && sessions.length > 0) {
          dispatch({ type: actions.SET_CURRENT_SESSION, payload: sessions[0].sessionId })
        }
      } catch (error) {
        console.error('Error loading chat sessions:', error)
        setChatSessions([])
      }
    }

    if (isOpen) {
      loadChatSessions()
    }
  }, [isOpen, dispatch, actions, state.currentSessionId, state.activeWorkspace])

  const loadWorkspaces = async () => {
    try {
      const response = await apiClient.getWorkspaces()
      const workspaceList = response?.workspaces || []
      setWorkspaces(workspaceList)
    } catch (error) {
      console.error('Error loading workspaces:', error)
      setWorkspaces([])
    }
  }

  useEffect(() => {
    loadWorkspaces()
  }, [])

  // Sync active workspace with API client for global headers
  useEffect(() => {
    const activeId = state.activeWorkspace?._id || state.activeWorkspace?.id || null
    apiClient.setActiveWorkspace(activeId)
  }, [state.activeWorkspace])

  // Create new chat session
  const createNewChat = async () => {
    try {
      const activeWorkspaceId = state.activeWorkspace?._id || state.activeWorkspace?.id || null
      const response = await apiClient.createChatSession('New Chat', activeWorkspaceId)
      const newSession = response.session

      setChatSessions(prev => [newSession, ...prev])
      dispatch({ type: actions.SET_CURRENT_SESSION, payload: newSession.sessionId })
      dispatch({ type: actions.CLEAR_CURRENT_CHAT })

      // Close sidebar on mobile after creating new chat
      if (window.innerWidth <= 768 && onToggle) {
        onToggle()
      }
    } catch (error) {
      console.error('Error creating new chat session:', error)
    }
  }

  // Select chat session
  const selectSession = async (sessionId) => {
    try {
      dispatch({ type: actions.SET_CURRENT_SESSION, payload: sessionId })

      const response = await apiClient.getChatSession(sessionId)
      if (response.session && response.session.messages) {
        dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: response.session.messages })
      } else {
        dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: [] })
      }

      // Close sidebar on mobile after selecting a chat
      if (window.innerWidth <= 768 && onToggle) {
        onToggle()
      }
    } catch (error) {
      console.error('Error loading session messages:', error)
      dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: [] })
    }
  }

  // Delete chat session
  const deleteSession = async (sessionId) => {
    try {
      await apiClient.deleteChatSession(sessionId)
      const updatedSessions = chatSessions.filter(s => (s.sessionId || s.id) !== sessionId)

      if (updatedSessions.length === 0) {
        setChatSessions([])
        await createNewChat()
        return
      }

      setChatSessions(updatedSessions)

      if (state.currentSessionId === sessionId) {
        const nextSession = updatedSessions[0]
        const nextSessionId = nextSession.sessionId || nextSession.id
        dispatch({ type: actions.SET_CURRENT_SESSION, payload: nextSessionId })
        dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: nextSession.messages || [] })
      }
    } catch (error) {
      console.error('Error deleting session:', error)
      alert('Error deleting chat session. Please try again.')
    }
  }

  // Rename chat session
  const renameSession = async (sessionId, newTitle) => {
    try {
      await apiClient.updateChatSession(sessionId, { title: newTitle })
      const updatedSessions = chatSessions.map(session => 
        (session.sessionId || session.id) === sessionId 
          ? { ...session, title: newTitle, updatedAt: new Date().toISOString() }
          : session
      )
      setChatSessions(updatedSessions)
      setEditingSession(null)
    } catch (error) {
      console.error('Error renaming session:', error)
      setEditingSession(null)
    }
  }

  const handleWorkspaceSelect = (workspace) => {
    const currentActiveId = state.activeWorkspace?._id || state.activeWorkspace?.id
    const newWorkspaceId = workspace?._id || workspace?.id
    if (currentActiveId === newWorkspaceId) return

    dispatch({ type: actions.SET_ACTIVE_WORKSPACE, payload: workspace })
    dispatch({ type: actions.CLEAR_CURRENT_CHAT })
    dispatch({ type: actions.SET_CURRENT_SESSION, payload: null })
  }

  // Get session preview
  const getSessionPreview = (session) => {
    if (session.title && session.title !== 'New Chat') {
      return session.title
    }
    
    const firstUserMessage = session.messages?.find(m => m.type === 'user')
    if (firstUserMessage) {
      return firstUserMessage.content.substring(0, 40) + (firstUserMessage.content.length > 40 ? '...' : '')
    }
    
    return 'New Chat'
  }

  // Get user info from auth context
  const userEmail = user?.email || state.userEmail || 'user@example.com'

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpenFor && !e.target.closest('.session-menu') && !e.target.closest('.session-menu-btn')) {
        setMenuOpenFor(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [menuOpenFor])

  return (
    <>
      {/* Collapsed Sidebar - Minimal Panel */}
      {!isOpen && (
        <div className="sidebar-collapsed">
          <div className="collapsed-top">
            <button 
              className="collapsed-btn"
              onClick={onToggle}
              title="Open sidebar"
            >
              <i className="fas fa-bars"></i>
            </button>
            <button 
              className="collapsed-btn"
              onClick={createNewChat}
              title="New chat"
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
          
          <div className="collapsed-bottom">
            <button 
              className="collapsed-btn user-btn"
              onClick={() => setShowUserSettings(true)}
              title={userEmail}
            >
              <div className="collapsed-avatar">
                <i className="fas fa-user"></i>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Full Sidebar */}
      <div className={`chat-history-sidebar ${isOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-content">
          {/* Header with Hamburger */}
          <div className="sidebar-header">
            <button 
              className="sidebar-toggle-btn"
              onClick={onToggle}
              title="Close sidebar"
            >
              <i className="fas fa-bars"></i>
            </button>
          </div>

          {/* New Chat Button */}
          <div className="sidebar-new-chat">
            <button 
              className="new-chat-button"
              onClick={createNewChat}
            >
              <i className="fas fa-plus"></i>
              <span>New chat</span>
            </button>
          </div>

          <div className="sidebar-scroll-area">
            <div className="sidebar-section-title admin-header">
              <span>Workspaces</span>
              {canCreateWorkspace && (
                <button
                  className="add-workspace-btn"
                  onClick={() => setShowCreateWorkspaceModal(true)}
                  title="Create New Workspace"
                >
                  <i className="fas fa-plus"></i>
                </button>
              )}
            </div>
            <button
              className={`workspace-item ${!state.activeWorkspace ? 'active' : ''}`}
              onClick={() => handleWorkspaceSelect(null)}
            >
              <span className="workspace-name">Company Context</span>
            </button>
            {workspaces.map((workspace) => (
              <button
                key={workspace._id || workspace.id}
                className={`workspace-item ${(state.activeWorkspace?._id || state.activeWorkspace?.id) === (workspace._id || workspace.id) ? 'active' : ''}`}
                onClick={() => handleWorkspaceSelect(workspace)}
              >
                <span className="workspace-name">{workspace.name}</span>
              </button>
            ))}

            <div className="sidebar-section-title">Chats</div>
            <div className="chat-sessions-list">
              {chatSessions.map((session) => (
                <div
                  key={session.sessionId || session.id}
                  className={`chat-session-item ${state.currentSessionId === (session.sessionId || session.id) ? 'active' : ''}`}
                  onClick={() => selectSession(session.sessionId || session.id)}
                >
                  <div className="session-content">
                    {editingSession === (session.sessionId || session.id) ? (
                      <input
                        type="text"
                        value={newSessionTitle}
                        onChange={(e) => setNewSessionTitle(e.target.value)}
                        onBlur={() => {
                          if (newSessionTitle.trim()) {
                            renameSession(session.sessionId || session.id, newSessionTitle.trim())
                          }
                          setEditingSession(null)
                          setNewSessionTitle('')
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (newSessionTitle.trim()) {
                              renameSession(session.sessionId || session.id, newSessionTitle.trim())
                            }
                            setEditingSession(null)
                            setNewSessionTitle('')
                          }
                          if (e.key === 'Escape') {
                            setEditingSession(null)
                            setNewSessionTitle('')
                          }
                        }}
                        autoFocus
                        className="session-title-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <div className="session-title">
                        {getSessionPreview(session)}
                      </div>
                    )}
                  </div>

                  <button
                    className="session-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      setMenuOpenFor(menuOpenFor === (session.sessionId || session.id) ? null : (session.sessionId || session.id))
                    }}
                  >
                    <i className="fas fa-ellipsis"></i>
                  </button>

                  {menuOpenFor === (session.sessionId || session.id) && (
                    <div className="session-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="menu-item"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingSession(session.sessionId || session.id)
                          setNewSessionTitle(session.title || getSessionPreview(session))
                          setMenuOpenFor(null)
                        }}
                      >
                        <i className="fas fa-pen"></i>
                        <span>Rename</span>
                      </button>
                      <button
                        className="menu-item delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (window.confirm('Delete chat?')) {
                            deleteSession(session.sessionId || session.id)
                          }
                          setMenuOpenFor(null)
                        }}
                      >
                        <i className="fas fa-trash"></i>
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* User Section */}
          <div className="sidebar-footer">
            <button
              className="user-section"
              onClick={() => {
                console.log('User profile clicked, opening settings')
                setShowUserSettings(true)
              }}
            >
              <div className="user-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="user-info">
                <div className="user-email">{userEmail}</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* User Settings Modal */}
      <UserSettings 
        isOpen={showUserSettings} 
        onClose={() => setShowUserSettings(false)} 
      />
      {showCreateWorkspaceModal && (
        <CreateWorkspaceModal
          isOpen={showCreateWorkspaceModal}
          onClose={() => setShowCreateWorkspaceModal(false)}
          onSuccess={() => {
            setShowCreateWorkspaceModal(false)
            loadWorkspaces()
          }}
        />
      )}
    </>
  )
}

export default ChatHistorySidebar
